import { supabase } from './supabase/client';

// Rule types and interfaces
export interface TransactionRule {
  id: string;
  student_id: string;
  name: string;
  description?: string;
  rule_type: 'amount' | 'category' | 'balance' | 'frequency';
  conditions: Record<string, any>;
  action: 'auto_pay' | 'defer' | 'save_first' | 'deny' | 'partial_pay';
  action_parameters: Record<string, any>;
  priority: number;
  is_active: boolean;
  times_applied: number;
  last_applied_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateRuleDTO {
  student_id: string;
  name: string;
  description?: string;
  rule_type: 'amount' | 'category' | 'balance' | 'frequency';
  conditions: Record<string, any>;
  action: 'auto_pay' | 'defer' | 'save_first' | 'deny' | 'partial_pay';
  action_parameters?: Record<string, any>;
  priority?: number;
}

export interface UpdateRuleDTO {
  id: string;
  name?: string;
  description?: string;
  conditions?: Record<string, any>;
  action?: 'auto_pay' | 'defer' | 'save_first' | 'deny' | 'partial_pay';
  action_parameters?: Record<string, any>;
  priority?: number;
  is_active?: boolean;
}

export interface RuleEvaluationContext {
  studentId: string;
  transactionType: string;
  transactionCategory: string;
  amount: number;
  currentBalance: number;
  dayOfWeek: number; // 0-6, Sunday = 0
  timeOfDay: number; // Hour in 24h format
}

export interface RuleEvaluationResult {
  action: string | null;
  rule_id: string | null;
  confidence: number; // 0-1, how confident we are in this match
  metadata: Record<string, any>;
  evaluated_at: string;
}

const TABLE_NAME = 'transaction_rules';

export const ruleEngineService = {
  /**
   * Create a new rule for a student
   */
  async createRule(dto: CreateRuleDTO): Promise<TransactionRule> {
    try {
      // Validate student exists
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('id')
        .eq('id', dto.student_id)
        .single();

      if (studentError) throw studentError;
      if (!student) throw new Error('Student not found');

      // Check if student already has max rules (10)
      const { count, error: countError } = await supabase
        .from(TABLE_NAME)
        .select('id', { count: 'exact' })
        .eq('student_id', dto.student_id)
        .eq('is_active', true);

      if (countError) throw countError;
      if (count && count >= 10) {
        throw new Error('Maximum number of active rules (10) reached');
      }

      // Validate rule conditions based on type
      this.validateRuleConditions(dto.rule_type, dto.conditions);

      const ruleData = {
        student_id: dto.student_id,
        name: dto.name,
        description: dto.description,
        rule_type: dto.rule_type,
        conditions: JSON.stringify(dto.conditions),
        action: dto.action,
        action_parameters: dto.action_parameters || {},
        priority: dto.priority || 1,
        is_active: true,
        times_applied: 0,
      };

      console.log('[RuleEngineService.createRule] Creating rule:', ruleData);

      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert(ruleData)
        .select()
        .single();

      if (error) throw error;
      return this.parseRule(data);

    } catch (error) {
      console.error('[RuleEngineService.createRule] Error:', error);
      throw error;
    }
  },

  /**
   * Get all rules for a student
   */
  async getStudentRules(studentId: string): Promise<TransactionRule[]> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('student_id', studentId)
        .order('priority', { ascending: true });

      if (error) throw error;
      return (data || []).map(this.parseRule);

    } catch (error) {
      console.error('[RuleEngineService.getStudentRules] Error:', error);
      throw error;
    }
  },

  /**
   * Update an existing rule
   */
  async updateRule(dto: UpdateRuleDTO): Promise<TransactionRule> {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      if (dto.name !== undefined) updateData.name = dto.name;
      if (dto.description !== undefined) updateData.description = dto.description;
      if (dto.action !== undefined) updateData.action = dto.action;
      if (dto.priority !== undefined) updateData.priority = dto.priority;
      if (dto.is_active !== undefined) updateData.is_active = dto.is_active;

      if (dto.conditions !== undefined) {
        // Get current rule to validate conditions with rule type
        const { data: currentRule, error: fetchError } = await supabase
          .from(TABLE_NAME)
          .select('rule_type')
          .eq('id', dto.id)
          .single();

        if (fetchError) throw fetchError;
        if (!currentRule) throw new Error('Rule not found');

        this.validateRuleConditions(currentRule.rule_type, dto.conditions);
        updateData.conditions = JSON.stringify(dto.conditions);
      }

      if (dto.action_parameters !== undefined) {
        updateData.action_parameters = JSON.stringify(dto.action_parameters);
      }

      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update(updateData)
        .eq('id', dto.id)
        .select()
        .single();

      if (error) throw error;
      return this.parseRule(data);

    } catch (error) {
      console.error('[RuleEngineService.updateRule] Error:', error);
      throw error;
    }
  },

  /**
   * Delete a rule
   */
  async deleteRule(ruleId: string, studentId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .delete()
        .eq('id', ruleId)
        .eq('student_id', studentId); // Ensure student owns the rule

      if (error) throw error;

    } catch (error) {
      console.error('[RuleEngineService.deleteRule] Error:', error);
      throw error;
    }
  },

  /**
   * Evaluate all rules for a transaction and return the best match
   */
  async evaluateRules(context: RuleEvaluationContext): Promise<RuleEvaluationResult> {
    try {
      // Get all active rules for the student, ordered by priority
      const { data: rules, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('student_id', context.studentId)
        .eq('is_active', true)
        .order('priority', { ascending: true });

      if (error) throw error;

      // Evaluate each rule
      for (const rule of rules || []) {
        const parsedRule = this.parseRule(rule);
        const evaluation = this.evaluateRule(parsedRule, context);

        if (evaluation.matches) {
          // Return the first matching rule (highest priority)
          return {
            action: parsedRule.action,
            rule_id: parsedRule.id,
            confidence: evaluation.confidence,
            metadata: {
              rule_name: parsedRule.name,
              rule_type: parsedRule.rule_type,
              conditions_met: evaluation.conditionsMet,
              action_parameters: parsedRule.action_parameters,
            },
            evaluated_at: new Date().toISOString(),
          };
        }
      }

      // No rules matched
      return {
        action: null,
        rule_id: null,
        confidence: 0,
        metadata: { rules_evaluated: rules?.length || 0 },
        evaluated_at: new Date().toISOString(),
      };

    } catch (error) {
      console.error('[RuleEngineService.evaluateRules] Error:', error);
      throw error;
    }
  },

  /**
   * Test a rule against sample scenarios
   */
  async testRule(ruleId: string, scenarios: RuleEvaluationContext[]): Promise<{
    ruleId: string;
    scenarios: Array<{
      scenario: RuleEvaluationContext;
      result: { matches: boolean; confidence: number; conditionsMet: string[] };
    }>;
  }> {
    try {
      const { data: ruleData, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('id', ruleId)
        .single();

      if (error) throw error;
      if (!ruleData) throw new Error('Rule not found');

      const rule = this.parseRule(ruleData);
      const results = scenarios.map(scenario => ({
        scenario,
        result: this.evaluateRule(rule, scenario),
      }));

      return {
        ruleId,
        scenarios: results,
      };

    } catch (error) {
      console.error('[RuleEngineService.testRule] Error:', error);
      throw error;
    }
  },

  /**
   * Get rule templates for common scenarios
   */
  getRuleTemplates(): Array<{
    name: string;
    description: string;
    rule_type: string;
    conditions: Record<string, any>;
    action: string;
    action_parameters: Record<string, any>;
  }> {
    return [
      {
        name: 'Auto-pay small fines',
        description: 'Automatically pay fines under $5',
        rule_type: 'amount',
        conditions: {
          operator: 'less_than',
          amount: 5,
          transaction_types: ['debit'],
        },
        action: 'auto_pay',
        action_parameters: {
          require_sufficient_balance: true,
        },
      },
      {
        name: 'Save first, then pay',
        description: 'Always save 20% before paying any fine',
        rule_type: 'category',
        conditions: {
          categories: ['fine', 'penalty'],
          save_percentage: 20,
        },
        action: 'save_first',
        action_parameters: {
          savings_percentage: 20,
          then_action: 'auto_pay',
        },
      },
      {
        name: 'Protect emergency fund',
        description: 'Defer payments that would reduce balance below $10',
        rule_type: 'balance',
        conditions: {
          min_balance_after: 10,
          protection_mode: true,
        },
        action: 'defer',
        action_parameters: {
          defer_days: 7,
          max_deferrals: 2,
        },
      },
      {
        name: 'Weekend auto-approval',
        description: 'Auto-approve all transactions on weekends',
        rule_type: 'frequency',
        conditions: {
          days_of_week: [0, 6], // Sunday and Saturday
          time_range: 'all_day',
        },
        action: 'auto_pay',
        action_parameters: {
          require_sufficient_balance: true,
        },
      },
    ];
  },

  /**
   * Get rule performance analytics for a student
   */
  async getRuleAnalytics(studentId: string): Promise<{
    totalRules: number;
    activeRules: number;
    totalApplications: number;
    ruleEffectiveness: Array<{
      ruleId: string;
      ruleName: string;
      timesApplied: number;
      lastApplied?: string;
      effectiveness: number; // 0-1 score
    }>;
  }> {
    try {
      const { data: rules, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('student_id', studentId);

      if (error) throw error;

      const analytics = {
        totalRules: rules?.length || 0,
        activeRules: rules?.filter(r => r.is_active).length || 0,
        totalApplications: rules?.reduce((sum, r) => sum + (r.times_applied || 0), 0) || 0,
        ruleEffectiveness: (rules || []).map(rule => ({
          ruleId: rule.id,
          ruleName: rule.name,
          timesApplied: rule.times_applied || 0,
          lastApplied: rule.last_applied_at,
          effectiveness: this.calculateRuleEffectiveness(rule),
        })),
      };

      return analytics;

    } catch (error) {
      console.error('[RuleEngineService.getRuleAnalytics] Error:', error);
      throw error;
    }
  },

  /**
   * Helper: Parse rule data from database
   */
  parseRule(data: any): TransactionRule {
    return {
      ...data,
      conditions: typeof data.conditions === 'string' ? JSON.parse(data.conditions) : data.conditions,
      action_parameters: typeof data.action_parameters === 'string' ?
        JSON.parse(data.action_parameters) : (data.action_parameters || {}),
    };
  },

  /**
   * Helper: Validate rule conditions based on rule type
   */
  validateRuleConditions(ruleType: string, conditions: Record<string, any>): void {
    switch (ruleType) {
      case 'amount':
        if (!conditions.operator || !['less_than', 'greater_than', 'equal_to', 'between'].includes(conditions.operator)) {
          throw new Error('Amount rules require a valid operator (less_than, greater_than, equal_to, between)');
        }
        if (conditions.amount === undefined || conditions.amount < 0) {
          throw new Error('Amount rules require a valid amount value');
        }
        break;

      case 'balance':
        if (conditions.min_balance_after === undefined || conditions.min_balance_after < 0) {
          throw new Error('Balance rules require a valid min_balance_after value');
        }
        break;

      case 'frequency':
        if (!conditions.days_of_week || !Array.isArray(conditions.days_of_week)) {
          throw new Error('Frequency rules require days_of_week array');
        }
        const validDays = conditions.days_of_week.every((day: number) => day >= 0 && day <= 6);
        if (!validDays) {
          throw new Error('days_of_week must contain valid day numbers (0-6)');
        }
        break;

      case 'category':
        if (!conditions.categories || !Array.isArray(conditions.categories)) {
          throw new Error('Category rules require categories array');
        }
        break;

      default:
        throw new Error(`Unsupported rule type: ${ruleType}`);
    }
  },

  /**
   * Helper: Evaluate a single rule against context
   */
  evaluateRule(rule: TransactionRule, context: RuleEvaluationContext): {
    matches: boolean;
    confidence: number;
    conditionsMet: string[];
  } {
    const conditionsMet: string[] = [];
    let confidence = 0;

    switch (rule.rule_type) {
      case 'amount':
        const { operator, amount } = rule.conditions;
        let amountMatch = false;

        switch (operator) {
          case 'less_than':
            amountMatch = context.amount < amount;
            break;
          case 'greater_than':
            amountMatch = context.amount > amount;
            break;
          case 'equal_to':
            amountMatch = Math.abs(context.amount - amount) < 0.01; // Handle floating point
            break;
          case 'between':
            amountMatch = context.amount >= rule.conditions.min && context.amount <= rule.conditions.max;
            break;
        }

        if (amountMatch) {
          conditionsMet.push(`amount_${operator}`);
          confidence += 0.8;
        }
        break;

      case 'balance':
        const balanceAfter = context.currentBalance - context.amount;
        if (balanceAfter >= rule.conditions.min_balance_after) {
          conditionsMet.push('balance_protection');
          confidence += 0.9;
        } else {
          conditionsMet.push('balance_violation');
          confidence += 0.9;
        }
        break;

      case 'frequency':
        if (rule.conditions.days_of_week.includes(context.dayOfWeek)) {
          conditionsMet.push('day_of_week_match');
          confidence += 0.7;
        }
        break;

      case 'category':
        if (rule.conditions.categories.includes(context.transactionCategory)) {
          conditionsMet.push('category_match');
          confidence += 0.8;
        }
        break;
    }

    // Additional confidence adjustments
    if (rule.times_applied > 0) {
      confidence += 0.1; // Boost for proven rules
    }

    return {
      matches: conditionsMet.length > 0 && confidence > 0.5,
      confidence: Math.min(confidence, 1.0),
      conditionsMet,
    };
  },

  /**
   * Helper: Calculate rule effectiveness score
   */
  calculateRuleEffectiveness(rule: any): number {
    const timesApplied = rule.times_applied || 0;
    const daysSinceCreated = rule.created_at ?
      Math.max(1, Math.floor((Date.now() - new Date(rule.created_at).getTime()) / (1000 * 60 * 60 * 24))) : 1;

    // Simple effectiveness: applications per day, capped at 1.0
    return Math.min(timesApplied / daysSinceCreated, 1.0);
  },
};